import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Code,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Progress,
  SimpleGrid,
  Switch,
  Stat,
  StatArrow,
  StatHelpText,
  StatLabel,
  StatNumber,
  Tab,
  Table,
  TableContainer,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
  VStack,
  Spinner,
} from '@chakra-ui/react';
import {
  AdminLayout,
  Button,
  Card,
  DataTable,
  SelectInput,
  StatsCard,
} from '@grcs/components';
import { TableColumn } from '@grcs/components/types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiBarChart,
  FiDownload,
  FiFileText,
  FiInfo,
  FiPieChart,
  FiPlay,
  FiRefreshCw,
  FiSave,
  FiTrendingUp,
} from 'react-icons/fi';
import hanzi_json from '../dictionary/hanzi_pinyin_list.json';
import hsk_json from '../dictionary/hsk_pinyin_list.json';
import { useUserOperations } from '../hook/useDatabase';
import { ReportType } from '../types/DataTypes';
import { calculateHanziUsage } from '../utils/content/calculateWords';

interface ReportAnalysis extends ReportType {
  analysisDate: string;
  totalCharacters: number;
  uniqueCharacters: number;
  coveragePercentage: number;
  difficultyScore: number;
  readabilityGrade: number;
}

interface ChapterComparison {
  chapter: string;
  coverage: number;
  difficulty: number;
  unknownWords: number;
  isSelected: boolean;
}

interface ExportData {
  reportId: string;
  inputText: string;
  outputText: string;
  mustWords: string;
  selectedChapter: string;
  characterLimit: number;
  analysis: ReportAnalysis;
  timestamp: string;
}

type GlossaryKey = 'GRCS' | 'HSK';
type GlossaryResults = Record<GlossaryKey, ReportType>;
type GlossaryAnalyses = Record<GlossaryKey, ReportAnalysis | null>;
type HanziChapterMap = Record<string, string[]>;
type HanziDictionary = Record<string, HanziChapterMap>;

const DEFAULT_REPORT: ReportType = {
  known_hanzi: '',
  known_hanzi_by_grade: {},
  unknown_hanzi: '',
  unknown_hanzi_overall: '',
  unknown_hanzi_usage: '',
  required_hanzi_frequency: {},
};

const DEFAULT_PROMPT_HELPER = `请严格遵守以下写作要求：
1. 创作一篇适合指定年级儿童阅读的中文短文。
2. 必须使用要求出现的字，并尽量自然重复这些字。
3. 不得出现暴力、打斗、伤害、恐怖等不适合儿童的内容。
4. 内容要有趣、合理、积极，适合儿童理解。
5. 全文必须控制在总字数上限以内。
6. 内容必须紧扣主题，不得偏题。
7. 只输出短文正文，不要输出标题、说明、序号或英文。`;

export const ReportEnhanced: React.FC = () => {
  const geminiApiKey = process.env.REACT_APP_GEMINI_API_KEY;
  const geminiModel =
    process.env.REACT_APP_GEMINI_MODEL || 'gemini-3-flash-preview';
  const userOps = useUserOperations();
  const toast = useToast();
  const {
    isOpen: isHistoryModalOpen,
    onOpen: onHistoryModalOpen,
    onClose: onHistoryModalClose,
  } = useDisclosure();

  // Form state
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [usableText, setUsableText] = useState<string>('');
  const [mustText, setMustText] = useState<string>('');
  const [selectedChapter, setSelectedChapter] = useState<string>('');
  const [characterLimit, setCharacterLimit] = useState<string>('400');
  const [comment, setComment] = useState<string>(DEFAULT_PROMPT_HELPER);
  const [useGeminiMode, setUseGeminiMode] = useState<boolean>(false);

  // Analysis state
  const [reportResults, setReportResults] = useState<GlossaryResults>({
    GRCS: DEFAULT_REPORT,
    HSK: DEFAULT_REPORT,
  });
  const [analysisResults, setAnalysisResults] = useState<GlossaryAnalyses>({
    GRCS: null,
    HSK: null,
  });
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isGeneratingOutput, setIsGeneratingOutput] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [analysisHistory, setAnalysisHistory] = useState<ExportData[]>([]);

  // UI state
  const [activeTab, setActiveTab] = useState(0);
  const [resultsGlossary, setResultsGlossary] = useState<GlossaryKey>('GRCS');

  const hanzi_list: HanziDictionary = hanzi_json;
  const hsk_list: HanziDictionary = hsk_json;
  const hasAnalysis = !!analysisResults.GRCS || !!analysisResults.HSK;
  const activeReportResult = reportResults[resultsGlossary];
  const activeAnalysisResult = analysisResults[resultsGlossary];
  const glossaryTabs = (
    <Tabs
      index={resultsGlossary === 'GRCS' ? 0 : 1}
      onChange={(index) => setResultsGlossary(index === 0 ? 'GRCS' : 'HSK')}
      size="sm"
      variant="enclosed"
      colorScheme="blue"
    >
      <TabList>
        <Tab>GRCS</Tab>
        <Tab>HSK</Tab>
      </TabList>
    </Tabs>
  );
  const reportResult = activeReportResult;
  const analysisResult = activeAnalysisResult;

  const getChapterCharacters = useCallback(
    (chapterKey: string) => {
      if (!chapterKey || !(chapterKey in hanzi_list)) {
        return '';
      }

      return Object.keys(hanzi_list[chapterKey]).join('');
    },
    [hanzi_list],
  );

  const getUsableCharacters = useCallback(
    (chapterKey: string) => {
      if (!chapterKey) {
        return '';
      }

      const uniqueCharacters = new Set<string>();
      Object.keys(hanzi_list).forEach((key) => {
        if (compareChapter(key, chapterKey) < 0) {
          Object.keys(hanzi_list[key]).forEach((character) => {
            uniqueCharacters.add(character);
          });
        }
      });

      return Array.from(uniqueCharacters).join('');
    },
    [hanzi_list],
  );

  useEffect(() => {
    if (!selectedChapter) {
      setUsableText('');
      setMustText('');
      return;
    }

    setUsableText(getUsableCharacters(selectedChapter));
    setMustText(getChapterCharacters(selectedChapter));
  }, [getChapterCharacters, getUsableCharacters, selectedChapter]);

  // Calculate chapter comparison data
  const chapterComparisons = useMemo(() => {
    if (!outputText || !selectedChapter) return [];

    const comparisons: ChapterComparison[] = [];
    Object.keys(hanzi_list).forEach((chapter) => {
      const tempValues = getSelectedValues(chapter, hanzi_list);
      const tempResult = calculateHanziUsage(outputText, mustText, tempValues);

      comparisons.push({
        chapter,
        coverage: parseFloat(tempResult.unknown_hanzi_overall) || 0,
        difficulty: Object.keys(tempResult.required_hanzi_frequency).length,
        unknownWords: tempResult.unknown_hanzi.length,
        isSelected: chapter === selectedChapter,
      });
    });
    const parseLevel = (level: string) => {
      const [gradePart, chapterPart] = level.split('.');
      return {
        grade: Number.parseInt(gradePart, 10),
        chapter: chapterPart ? Number.parseInt(chapterPart, 10) : 0,
      };
    };
    // Sort by grade, then chapter
    comparisons.sort((a, b) => {
      const levelA = parseLevel(a.chapter);
      const levelB = parseLevel(b.chapter);
      if (levelA.grade !== levelB.grade) {
        return levelA.grade - levelB.grade;
      }
      return levelA.chapter - levelB.chapter;
    });

    return comparisons;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outputText, mustText, selectedChapter, hanzi_list]);

  const hskComparisons = useMemo(() => {
    if (!outputText) return [];

    const comparisons: ChapterComparison[] = [];
    Object.keys(hsk_list).forEach((level) => {
      const tempValues = getHskSelectedValues(level, hsk_list);
      const tempResult = calculateHanziUsage(outputText, mustText, tempValues);

      comparisons.push({
        chapter: level,
        coverage: parseFloat(tempResult.unknown_hanzi_overall) || 0,
        difficulty: Object.keys(tempResult.required_hanzi_frequency).length,
        unknownWords: tempResult.unknown_hanzi.length,
        isSelected: false,
      });
    });

    comparisons.sort((a, b) => Number(a.chapter) - Number(b.chapter));
    return comparisons;
  }, [outputText, mustText, hsk_list]);

  const buildAnalysis = (
    result: ReportType,
    fallbackGrade: number,
    analyzedText: string,
  ): ReportAnalysis => {
    const totalChars = analyzedText.length;
    const uniqueChars = new Set(analyzedText).size;
    const knownCharsCount = result.known_hanzi.length;
    const unknownCharsCount = result.unknown_hanzi.length;
    const coveragePercentage =
      totalChars > 0 ? (knownCharsCount / totalChars) * 100 : 0;
    const difficultyScore = Math.min(
      100,
      (unknownCharsCount / totalChars) * 100 * 10,
    );
    const readabilityGrade = fallbackGrade || 1;

    return {
      ...result,
      analysisDate: new Date().toISOString(),
      totalCharacters: totalChars,
      uniqueCharacters: uniqueChars,
      coveragePercentage,
      difficultyScore,
      readabilityGrade,
    };
  };

  const buildGeminiPrompt = useCallback(() => {
    const parsedLimit = Number.parseInt(characterLimit, 10);
    const safeCharacterLimit = Number.isNaN(parsedLimit) ? 400 : parsedLimit;
    const requiredCharacters = mustText
      .split('')
      .map((char) => char.trim())
      .filter(Boolean)
      .join('、');

    return `你是一位儿童中文写作助手。请根据以下要求，创作一篇适合儿童阅读的中文短文。

【写作任务】
主题：${inputText}
适用年级/课程：${selectedChapter}
可使用的参考汉字：${usableText || '无'}
必须使用的字：${requiredCharacters || '无'}
总字数上限：${safeCharacterLimit}字

【写作标准】
1. 请创作一篇适合指定年级儿童阅读的短文。
2. 必须自然使用这些要求出现的字：${
      requiredCharacters || '无'
    }。请尽量多次重复使用这些字，但文章必须通顺自然。
3. 文章不得包含暴力、打斗、伤害、恐怖或其他不适合儿童的情节。
4. 内容要有趣、合理、积极，符合儿童的理解能力和阅读兴趣。
5. 全文总字数不得超过${safeCharacterLimit}字。
6. 文章内容必须紧扣主题“${inputText}”，不得偏题。
7. 请优先使用这些较低年级已经学过的汉字作为主要用字范围：${usableText || '无'}。

【提示补充】
${comment.trim() || '无'}

【输出要求】
1. 只输出短文正文，不要输出标题、说明、注释、序号或英文。
2. 使用简体中文。
3. 语言自然，句子清楚，逻辑完整。
4. 在保证自然流畅的前提下，优先提高要求出现的字的重复率。`;
  }, [
    characterLimit,
    comment,
    inputText,
    mustText,
    selectedChapter,
    usableText,
  ]);

  const generateOutputWithGemini = useCallback(async (): Promise<string> => {
    if (process.env.REACT_APP_DATA_ENV === 'local') {
      return '今天小明去学校。他和朋友一起读书，一起学习中文。放学以后，他们高高兴兴地回家。';
    }
    if (!geminiApiKey) {
      throw new Error('Missing REACT_APP_GEMINI_API_KEY');
    }

    const prompt = buildGeminiPrompt();

    const response = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/${geminiModel}:generateContent`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-goog-api-key': geminiApiKey,
        },
        body: JSON.stringify({
          contents: [
            {
              role: 'user',
              parts: [{ text: prompt }],
            },
          ],
        }),
      },
    );

    const data = await response.json();

    if (!response.ok) {
      throw new Error(
        data?.error?.message || 'Gemini request failed. Please try again.',
      );
    }

    const generatedText =
      data?.candidates
        ?.flatMap((candidate: any) => candidate?.content?.parts ?? [])
        ?.map((part: any) => part?.text ?? '')
        ?.join('')
        ?.trim() || '';

    if (!generatedText) {
      throw new Error('Gemini returned an empty response.');
    }

    return generatedText;
  }, [buildGeminiPrompt, geminiApiKey, geminiModel]);

  // Enhanced analysis function
  const performAnalysis = useCallback(async () => {
    const parsedLimit = Number.parseInt(characterLimit, 10);
    const missingRequiredFields = useGeminiMode
      ? !inputText ||
        !mustText ||
        !selectedChapter ||
        Number.isNaN(parsedLimit) ||
        parsedLimit <= 0
      : !inputText || !outputText || !mustText || !selectedChapter;

    if (missingRequiredFields) {
      toast({
        title: 'Missing required fields',
        description: useGeminiMode
          ? 'Please fill in the prompt, required characters, target chapter, and a valid character limit before generating report.'
          : 'Please fill in the prompt, analysis text, required characters, and target chapter before generating report.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsAnalyzing(true);
    try {
      if (useGeminiMode) {
        setIsGeneratingOutput(true);
      }

      const textForAnalysis = useGeminiMode
        ? await generateOutputWithGemini()
        : outputText;

      if (useGeminiMode) {
        setOutputText(textForAnalysis);
      }

      const selectedValues = getSelectedValues(selectedChapter, hanzi_list);
      const grcsResult = calculateHanziUsage(
        textForAnalysis,
        mustText,
        selectedValues,
      );
      const hskResult = calculateHanziUsage(
        textForAnalysis,
        mustText,
        hsk_list,
      );

      const grcsGrade = parseFloat(selectedChapter.split('.')[0]) || 1;
      const hskGrade =
        Math.max(
          ...Object.keys(hskResult.known_hanzi_by_grade).map((grade) =>
            Number.parseInt(grade, 10),
          ),
          1,
        ) || 1;

      const grcsAnalysis = buildAnalysis(
        grcsResult,
        grcsGrade,
        textForAnalysis,
      );
      const hskAnalysis = buildAnalysis(hskResult, hskGrade, textForAnalysis);

      setReportResults({
        GRCS: grcsResult,
        HSK: hskResult,
      });
      setAnalysisResults({
        GRCS: grcsAnalysis,
        HSK: hskAnalysis,
      });
      setActiveTab(1); // Switch to results tab

      toast({
        title: 'Analysis complete',
        description: `${
          useGeminiMode ? 'Gemini generated and analyzed' : 'Analyzed'
        } ${
          grcsAnalysis.totalCharacters
        } characters with ${grcsAnalysis.coveragePercentage.toFixed(
          1,
        )}% coverage.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Analysis error:', error);
      toast({
        title: 'Analysis failed',
        description:
          error instanceof Error
            ? error.message
            : 'An error occurred during text analysis. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsGeneratingOutput(false);
      setIsAnalyzing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    characterLimit,
    generateOutputWithGemini,
    inputText,
    mustText,
    outputText,
    selectedChapter,
    toast,
    useGeminiMode,
    hanzi_list,
    hsk_list,
  ]);

  // Save to database
  const saveReport = useCallback(async () => {
    const primaryAnalysis = analysisResults.GRCS || analysisResults.HSK;
    const primaryResult = reportResults.GRCS || reportResults.HSK;
    if (!primaryAnalysis) {
      toast({
        title: 'No analysis to save',
        description: 'Please generate a report first.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSaving(true);
    try {
      await userOps.insertData('gpt_result', {
        input: inputText,
        output: outputText,
        appeared_words: mustText,
        must_words: primaryResult.required_hanzi_frequency,
        comment: comment,
        unknown_words: primaryResult.unknown_hanzi,
        vocabulary: primaryResult.known_hanzi_by_grade,
        known_hanzi: primaryResult.known_hanzi,
        unknown_hanzi_overall: primaryResult.unknown_hanzi_overall,
        unknown_hanzi_usage: primaryResult.unknown_hanzi_usage,
        deleted: false,
      });

      // Add to history
      const exportData: ExportData = {
        reportId: Date.now().toString(),
        inputText,
        outputText,
        mustWords: mustText,
        selectedChapter,
        characterLimit: Number.parseInt(characterLimit, 10) || 0,
        analysis: primaryAnalysis,
        timestamp: new Date().toISOString(),
      };
      setAnalysisHistory((prev) => [exportData, ...prev.slice(0, 9)]);

      toast({
        title: 'Report saved',
        description: 'Analysis has been saved to the database successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: 'Save failed',
        description: 'Failed to save the report. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    analysisResults.GRCS,
    analysisResults.HSK,
    toast,
    userOps,
    inputText,
    outputText,
    mustText,
    reportResults.GRCS,
    reportResults.HSK,
    comment,
    characterLimit,
    selectedChapter,
  ]);

  // Export functionality
  const exportReport = useCallback(
    (format: 'json' | 'csv') => {
      const primaryAnalysis = analysisResults.GRCS || analysisResults.HSK;
      if (!primaryAnalysis) return;

      const exportData: ExportData = {
        reportId: Date.now().toString(),
        inputText,
        outputText,
        mustWords: mustText,
        selectedChapter,
        characterLimit: Number.parseInt(characterLimit, 10) || 0,
        analysis: primaryAnalysis,
        timestamp: new Date().toISOString(),
      };

      let content: string;
      let filename: string;
      let mimeType: string;

      if (format === 'json') {
        content = JSON.stringify(exportData, null, 2);
        filename = `hanzi-analysis-${Date.now()}.json`;
        mimeType = 'application/json';
      } else {
        // CSV format
        const csvRows = [
          ['Field', 'Value'],
          ['Report ID', exportData.reportId],
          ['Analysis Date', new Date(exportData.timestamp).toLocaleString()],
          ['Selected Chapter', exportData.selectedChapter],
          ['Character Limit', exportData.characterLimit.toString()],
          ['Input Text Length', exportData.inputText.length.toString()],
          ['Output Text Length', exportData.outputText.length.toString()],
          ['Must Words', exportData.mustWords],
          ['Total Characters', exportData.analysis.totalCharacters.toString()],
          [
            'Unique Characters',
            exportData.analysis.uniqueCharacters.toString(),
          ],
          [
            'Coverage Percentage',
            exportData.analysis.coveragePercentage.toFixed(2) + '%',
          ],
          ['Difficulty Score', exportData.analysis.difficultyScore.toFixed(2)],
          ['Known Hanzi', exportData.analysis.known_hanzi],
          ['Unknown Hanzi', exportData.analysis.unknown_hanzi],
          [
            'Unknown Hanzi Overall %',
            exportData.analysis.unknown_hanzi_overall,
          ],
          ['Unknown Hanzi Usage %', exportData.analysis.unknown_hanzi_usage],
        ];
        content = csvRows
          .map((row) =>
            row.map((field) => `"${field.replace(/"/g, '""')}"`).join(','),
          )
          .join('\n');
        filename = `hanzi-analysis-${Date.now()}.csv`;
        mimeType = 'text/csv';
      }

      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: 'Export successful',
        description: `Report exported as ${filename}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
    [
      analysisResults.GRCS,
      analysisResults.HSK,
      inputText,
      outputText,
      mustText,
      characterLimit,
      selectedChapter,
      toast,
    ],
  );

  // Clear form
  const clearForm = useCallback(() => {
    setInputText('');
    setOutputText('');
    setUsableText('');
    setMustText('');
    setSelectedChapter('');
    setCharacterLimit('400');
    setComment(DEFAULT_PROMPT_HELPER);
    setReportResults({ GRCS: DEFAULT_REPORT, HSK: DEFAULT_REPORT });
    setAnalysisResults({ GRCS: null, HSK: null });
    setActiveTab(0);
  }, []);

  // Helper functions
  function compareChapter(list1: string, list2: string): number {
    const [grade1, chapter1] = list1.split('.').map(Number);
    const [grade2, chapter2] = list2.split('.').map(Number);
    if (grade1 !== grade2) {
      return grade1 - grade2;
    }
    return chapter1 - chapter2;
  }

  function getSelectedValues(
    selectedKey: string,
    hanziList: Record<string, Record<string, string[]>>,
  ) {
    const selectedValues: Record<string, Record<string, string[]>> = {};
    if (selectedKey in hanziList) {
      for (const key in hanziList) {
        const result = compareChapter(key, selectedKey);
        if (isNaN(result) || result <= 0) {
          selectedValues[key] = hanziList[key];
        }
      }
    }
    return selectedValues;
  }

  function getHskSelectedValues(
    selectedLevel: string,
    hanziList: Record<string, Record<string, string[]>>,
  ) {
    const selectedValues: Record<string, Record<string, string[]>> = {};
    const selectedNumeric = Number.parseInt(selectedLevel, 10);
    if (Number.isNaN(selectedNumeric)) {
      return selectedValues;
    }
    for (const key in hanziList) {
      const levelNumber = Number.parseInt(key, 10);
      if (!Number.isNaN(levelNumber) && levelNumber <= selectedNumeric) {
        selectedValues[key] = hanziList[key];
      }
    }
    return selectedValues;
  }

  // Table columns for frequency data
  const frequencyColumns: TableColumn<[string, number]>[] = [
    {
      key: '0',
      header: 'Character',
      render: ([char]) => (
        <Text fontWeight="bold" fontSize="lg" color="blue.600">
          {char}
        </Text>
      ),
    },
    {
      key: '1',
      header: 'Frequency',
      render: ([, freq]) => (
        <Badge colorScheme={freq >= 5 ? 'green' : freq >= 2 ? 'yellow' : 'red'}>
          {freq} times
        </Badge>
      ),
    },
  ];

  const gradeColumns: TableColumn<[string, number]>[] = [
    {
      key: '0',
      header: 'Grade Level',
      render: ([grade]) => <Text fontWeight="medium">Grade {grade}</Text>,
    },
    {
      key: '1',
      header: 'Character Count',
      render: ([, count]) => (
        <Badge colorScheme="blue">{count} characters</Badge>
      ),
    },
  ];

  return (
    <AdminLayout>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between" align="center">
          <Box>
            <Text fontSize="2xl" fontWeight="bold" color="gray.800">
              Create AI Log
            </Text>
            <Text color="gray.600">
              Generate AI analysis results and save to database
            </Text>
          </Box>
          <HStack spacing={3}>
            <Tooltip label="View Analysis History">
              <IconButton
                aria-label="View history"
                icon={<FiFileText />}
                onClick={onHistoryModalOpen}
                variant="outline"
              />
            </Tooltip>
            <Button
              leftIcon={<FiRefreshCw />}
              onClick={clearForm}
              variant="secondary"
            >
              Clear All
            </Button>
          </HStack>
        </HStack>

        {/* Main Content */}
        <Tabs
          index={activeTab}
          onChange={setActiveTab}
          variant="line"
          colorScheme="blue"
        >
          <TabList>
            <Tab>Input & Configuration</Tab>
            <Tab isDisabled={!hasAnalysis}>Analysis Results</Tab>
            <Tab isDisabled={!hasAnalysis}>Advanced Analytics</Tab>
            <Tab isDisabled={!hasAnalysis}>Chapter Comparison</Tab>
          </TabList>

          <TabPanels>
            {/* Input Tab */}
            <TabPanel px={0}>
              <VStack spacing={6} align="stretch">
                <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                  <Card>
                    <VStack spacing={4} align="stretch">
                      <Text fontWeight="bold" fontSize="lg">
                        Input Configuration
                      </Text>

                      <HStack justify="space-between" align="center">
                        <Box>
                          <Text fontSize="sm" fontWeight="medium">
                            Gemini Mode
                          </Text>
                        </Box>
                        <Switch
                          colorScheme="blue"
                          isChecked={useGeminiMode}
                          onChange={(e) => setUseGeminiMode(e.target.checked)}
                        />
                      </HStack>

                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={2}>
                          {useGeminiMode
                            ? '主题*'
                            : 'Prompt Input (提示输入) *'}
                        </Text>
                        <Textarea
                          value={inputText}
                          onChange={(e) => setInputText(e.target.value)}
                          placeholder={
                            useGeminiMode
                              ? '文章生成主题，例如：春天的公园、我的一天、神奇的动物等...'
                              : 'Enter the input prompt for analysis...'
                          }
                          rows={4}
                          resize="vertical"
                        />
                      </Box>

                      <SelectInput
                        label="目标课程单元"
                        value={selectedChapter}
                        onChange={(value) => setSelectedChapter(value)}
                        placeholder="Select target chapter"
                        options={Object.keys(hanzi_list).map((key) => ({
                          value: key,
                          label: `Chapter ${key}`,
                        }))}
                        isRequired
                      />

                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={2}>
                          可使用汉字*
                        </Text>
                        <Textarea
                          value={usableText}
                          onChange={(e) => setUsableText(e.target.value)}
                          rows={3}
                          resize="vertical"
                        />
                      </Box>

                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={2}>
                          要求出现汉字*
                        </Text>
                        <Textarea
                          value={mustText}
                          onChange={(e) => setMustText(e.target.value)}
                          rows={1}
                          resize="vertical"
                        />
                      </Box>

                      {useGeminiMode && (
                        <>
                          <Box>
                            <Text fontSize="sm" fontWeight="medium" mb={2}>
                              总字数上限*
                            </Text>
                            <Input
                              type="number"
                              min={1}
                              value={characterLimit}
                              onChange={(e) =>
                                setCharacterLimit(e.target.value)
                              }
                              placeholder="Enter total character limit"
                            />
                          </Box>

                          <Box>
                            <Text fontSize="sm" fontWeight="medium" mb={2}>
                              提示辅助词
                            </Text>
                            <Textarea
                              value={comment}
                              onChange={(e) => setComment(e.target.value)}
                              placeholder="Edit the helper instructions that will be appended to the Gemini prompt..."
                              rows={10}
                              resize="vertical"
                            />
                          </Box>
                        </>
                      )}
                    </VStack>
                  </Card>

                  <Card>
                    <VStack spacing={4} align="stretch">
                      <Text fontWeight="bold" fontSize="lg">
                        {useGeminiMode
                          ? '提示词输入并生成文本'
                          : 'Analysis Text'}
                      </Text>

                      {useGeminiMode && (
                        <Box>
                          <Text fontSize="sm" fontWeight="medium" mb={2}>
                            完整提示词
                          </Text>
                          <Textarea
                            value={buildGeminiPrompt()}
                            isReadOnly
                            rows={12}
                            resize="vertical"
                          />
                        </Box>
                      )}

                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={2}>
                          {useGeminiMode
                            ? 'Gemini 生成文本'
                            : 'Text to Analyze (分析文本) *'}
                        </Text>
                        <Textarea
                          value={outputText}
                          onChange={(e) => setOutputText(e.target.value)}
                          placeholder={
                            useGeminiMode
                              ? 'Gemini will generate the analysis text here from the prompt input. You can also edit it manually.'
                              : 'Enter the text to be analyzed...'
                          }
                          rows={12}
                          resize="vertical"
                        />
                        {useGeminiMode && (
                          <HStack mt={2} spacing={2}>
                            {isGeneratingOutput && (
                              <Spinner size="sm" color="blue.500" />
                            )}
                            <Text fontSize="xs" color="gray.500">
                              {isGeneratingOutput
                                ? 'Waiting for Gemini response...'
                                : 'The prompt input is sent to Gemini, then the returned text is analyzed below.'}
                            </Text>
                          </HStack>
                        )}
                      </Box>

                      <HStack spacing={3}>
                        <Text fontSize="sm" color="gray.600">
                          Characters: {outputText.length}
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          Words:{' '}
                          {
                            outputText.split(/\s+/).filter((w) => w.length > 0)
                              .length
                          }
                        </Text>
                        <Text fontSize="sm" color="gray.600">
                          Unique: {new Set(outputText).size}
                        </Text>
                      </HStack>
                    </VStack>
                  </Card>
                </SimpleGrid>

                <Card>
                  <HStack justify="space-between" align="center">
                    <HStack spacing={3}>
                      <Button
                        leftIcon={<FiPlay />}
                        onClick={performAnalysis}
                        colorScheme="blue"
                        size="lg"
                        isLoading={isAnalyzing}
                        loadingText="Analyzing..."
                        isDisabled={
                          !inputText ||
                          !mustText ||
                          !selectedChapter ||
                          (useGeminiMode ? !characterLimit : !outputText)
                        }
                      >
                        {useGeminiMode ? 'Generate Report' : 'Analyze Text'}
                      </Button>
                      <Text fontSize="sm" color="gray.600">
                        {useGeminiMode
                          ? 'Generate text with Gemini and analyze character usage'
                          : 'Analyze the manually entered text using the legacy workflow'}
                      </Text>
                    </HStack>
                  </HStack>
                </Card>
              </VStack>
            </TabPanel>

            {/* Results Tab */}
            <TabPanel px={0}>
              {hasAnalysis && (
                <VStack spacing={6} align="stretch">
                  <Box>{glossaryTabs}</Box>
                  {activeAnalysisResult ? (
                    <>
                      {/* Summary Stats */}
                      <SimpleGrid
                        columns={{ base: 1, md: 2, lg: 4 }}
                        spacing={4}
                      >
                        <StatsCard
                          title="Coverage Rate"
                          value={`${activeAnalysisResult.coveragePercentage.toFixed(
                            1,
                          )}%`}
                          subtitle="Characters in curriculum"
                        />
                        <StatsCard
                          title="Difficulty Score"
                          value={activeAnalysisResult.difficultyScore.toFixed(
                            1,
                          )}
                          subtitle="Lower is easier"
                        />
                        <StatsCard
                          title="Total Characters"
                          value={activeAnalysisResult.totalCharacters.toString()}
                          subtitle={`${activeAnalysisResult.uniqueCharacters} unique`}
                        />
                        <StatsCard
                          title="Target Grade"
                          value={activeAnalysisResult.readabilityGrade.toString()}
                          subtitle="Reading level"
                        />
                      </SimpleGrid>

                      {/* Detailed Results */}
                      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                        {/* Character Frequency */}
                        <Card>
                          <VStack spacing={4} align="stretch">
                            <HStack justify="space-between">
                              <Text fontWeight="bold" fontSize="lg">
                                Required Character Frequency
                              </Text>
                              <Badge colorScheme="blue">
                                {
                                  Object.keys(
                                    activeReportResult.required_hanzi_frequency,
                                  ).length
                                }{' '}
                                chars
                              </Badge>
                            </HStack>

                            {Object.keys(
                              activeReportResult.required_hanzi_frequency,
                            ).length > 0 ? (
                              <DataTable
                                data={Object.entries(
                                  activeReportResult.required_hanzi_frequency,
                                )}
                                columns={frequencyColumns}
                              />
                            ) : (
                              <Text color="gray.500" textAlign="center" py={4}>
                                No required characters found
                              </Text>
                            )}
                          </VStack>
                        </Card>

                        {/* Grade Distribution */}
                        <Card>
                          <VStack spacing={4} align="stretch">
                            <HStack justify="space-between">
                              <Text fontWeight="bold" fontSize="lg">
                                Grade Level Distribution
                              </Text>
                              <Badge colorScheme="green">
                                {
                                  Object.keys(
                                    activeReportResult.known_hanzi_by_grade,
                                  ).length
                                }{' '}
                                grades
                              </Badge>
                            </HStack>

                            {Object.keys(
                              activeReportResult.known_hanzi_by_grade,
                            ).length > 0 ? (
                              <DataTable
                                data={Object.entries(
                                  activeReportResult.known_hanzi_by_grade,
                                )}
                                columns={gradeColumns}
                              />
                            ) : (
                              <Text color="gray.500" textAlign="center" py={4}>
                                No grade data available
                              </Text>
                            )}
                          </VStack>
                        </Card>
                      </SimpleGrid>

                      {/* Character Analysis */}
                      <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={6}>
                        <Card>
                          <VStack spacing={3} align="stretch">
                            <Text fontWeight="bold">
                              Known Characters (课表字)
                            </Text>
                            <Code
                              fontSize="sm"
                              p={3}
                              maxH="200px"
                              overflowY="auto"
                            >
                              {activeReportResult.known_hanzi || 'None'}
                            </Code>
                            <Text fontSize="sm" color="gray.600">
                              Characters found in curriculum
                            </Text>
                          </VStack>
                        </Card>

                        <Card>
                          <VStack spacing={3} align="stretch">
                            <Text fontWeight="bold">
                              Unknown Characters (生僻字)
                            </Text>
                            <Code
                              fontSize="sm"
                              p={3}
                              maxH="200px"
                              overflowY="auto"
                              colorScheme="red"
                            >
                              {activeReportResult.unknown_hanzi || 'None'}
                            </Code>
                            <Text fontSize="sm" color="gray.600">
                              Characters not in curriculum
                            </Text>
                          </VStack>
                        </Card>

                        <Card>
                          <VStack spacing={3} align="stretch">
                            <Text fontWeight="bold">Usage Statistics</Text>
                            <VStack spacing={2} align="start">
                              <HStack justify="space-between" w="full">
                                <Text fontSize="sm">Unknown Overall:</Text>
                                <Badge colorScheme="orange">
                                  {activeReportResult.unknown_hanzi_overall}
                                </Badge>
                              </HStack>
                              <HStack justify="space-between" w="full">
                                <Text fontSize="sm">Unknown Usage:</Text>
                                <Badge colorScheme="red">
                                  {activeReportResult.unknown_hanzi_usage}
                                </Badge>
                              </HStack>
                              <Progress
                                value={
                                  parseFloat(
                                    activeReportResult.unknown_hanzi_overall,
                                  ) || 0
                                }
                                colorScheme="orange"
                                size="sm"
                                w="full"
                              />
                            </VStack>
                          </VStack>
                        </Card>
                      </SimpleGrid>

                      {/* Action Buttons */}
                      <Card>
                        <HStack spacing={4} justify="center">
                          <Button
                            leftIcon={<FiSave />}
                            onClick={saveReport}
                            colorScheme="green"
                            isLoading={isSaving}
                            loadingText="Saving..."
                          >
                            Save to Database
                          </Button>
                          <Button
                            leftIcon={<FiDownload />}
                            onClick={() => exportReport('json')}
                            variant="secondary"
                          >
                            Export JSON
                          </Button>
                          <Button
                            leftIcon={<FiDownload />}
                            onClick={() => exportReport('csv')}
                            variant="secondary"
                          >
                            Export CSV
                          </Button>
                        </HStack>
                      </Card>
                    </>
                  ) : (
                    <Card>
                      <VStack spacing={4} py={8}>
                        <Text color="gray.600">
                          No results available for this glossary.
                        </Text>
                      </VStack>
                    </Card>
                  )}
                </VStack>
              )}
            </TabPanel>

            {/* Advanced Analytics Tab */}
            <TabPanel px={0}>
              {hasAnalysis && (
                <VStack spacing={6} align="stretch">
                  <Box>{glossaryTabs}</Box>
                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                    {/* Complexity Analysis */}
                    <Card>
                      <VStack spacing={4} align="stretch">
                        <HStack>
                          <FiBarChart />
                          <Text fontWeight="bold" fontSize="lg">
                            Complexity Analysis
                          </Text>
                        </HStack>

                        <VStack spacing={3}>
                          <Box w="full">
                            <HStack justify="space-between" mb={2}>
                              <Text fontSize="sm">Character Diversity</Text>
                              <Text fontSize="sm" fontWeight="bold">
                                {analysisResult
                                  ? (
                                      (analysisResult.uniqueCharacters /
                                        analysisResult.totalCharacters) *
                                      100
                                    ).toFixed(1)
                                  : '0'}
                                %
                              </Text>
                            </HStack>
                            <Progress
                              value={
                                analysisResult
                                  ? (analysisResult.uniqueCharacters /
                                      analysisResult.totalCharacters) *
                                    100
                                  : 0
                              }
                              colorScheme="blue"
                            />
                          </Box>

                          <Box w="full">
                            <HStack justify="space-between" mb={2}>
                              <Text fontSize="sm">Curriculum Coverage</Text>
                              <Text fontSize="sm" fontWeight="bold">
                                {analysisResult
                                  ? analysisResult.coveragePercentage.toFixed(1)
                                  : '0'}
                                %
                              </Text>
                            </HStack>
                            <Progress
                              value={
                                analysisResult
                                  ? analysisResult.coveragePercentage
                                  : 0
                              }
                              colorScheme="green"
                            />
                          </Box>

                          <Box w="full">
                            <HStack justify="space-between" mb={2}>
                              <Text fontSize="sm">Difficulty Level</Text>
                              <Text fontSize="sm" fontWeight="bold">
                                {analysisResult &&
                                analysisResult.difficultyScore > 70
                                  ? 'High'
                                  : analysisResult &&
                                    analysisResult.difficultyScore > 30
                                  ? 'Medium'
                                  : 'Low'}
                              </Text>
                            </HStack>
                            <Progress
                              value={
                                analysisResult
                                  ? analysisResult.difficultyScore
                                  : 0
                              }
                              colorScheme={
                                analysisResult &&
                                analysisResult.difficultyScore > 70
                                  ? 'red'
                                  : analysisResult &&
                                    analysisResult.difficultyScore > 30
                                  ? 'yellow'
                                  : 'green'
                              }
                            />
                          </Box>
                        </VStack>
                      </VStack>
                    </Card>

                    {/* Readability Metrics */}
                    <Card>
                      <VStack spacing={4} align="stretch">
                        <HStack>
                          <FiTrendingUp />
                          <Text fontWeight="bold" fontSize="lg">
                            Readability Metrics
                          </Text>
                        </HStack>

                        <SimpleGrid columns={2} spacing={4}>
                          <Stat>
                            <StatLabel>Target Grade</StatLabel>
                            <StatNumber>
                              {analysisResult
                                ? analysisResult.readabilityGrade
                                : 'N/A'}
                            </StatNumber>
                            <StatHelpText>Reading level</StatHelpText>
                          </Stat>

                          <Stat>
                            <StatLabel>Text Length</StatLabel>
                            <StatNumber>
                              {analysisResult
                                ? analysisResult.totalCharacters
                                : 'N/A'}
                            </StatNumber>
                            <StatHelpText>Total characters</StatHelpText>
                          </Stat>

                          <Stat>
                            <StatLabel>Vocabulary</StatLabel>
                            <StatNumber>
                              {analysisResult
                                ? analysisResult.uniqueCharacters
                                : 'N/A'}
                            </StatNumber>
                            <StatHelpText>Unique characters</StatHelpText>
                          </Stat>

                          <Stat>
                            <StatLabel>Unknown Rate</StatLabel>
                            <StatNumber>
                              {reportResult.unknown_hanzi_overall}
                            </StatNumber>
                            <StatHelpText>
                              <StatArrow
                                type={
                                  parseFloat(
                                    reportResult.unknown_hanzi_overall,
                                  ) > 10
                                    ? 'increase'
                                    : 'decrease'
                                }
                              />
                              {parseFloat(reportResult.unknown_hanzi_overall) >
                              10
                                ? 'High'
                                : 'Low'}
                            </StatHelpText>
                          </Stat>
                        </SimpleGrid>
                      </VStack>
                    </Card>
                  </SimpleGrid>

                  {/* Detailed Character Breakdown */}
                  <Card>
                    <VStack spacing={4} align="stretch">
                      <Text fontWeight="bold" fontSize="lg">
                        Character Usage Breakdown
                      </Text>

                      <Accordion allowToggle>
                        <AccordionItem>
                          <AccordionButton>
                            <Box flex="1" textAlign="left">
                              <HStack>
                                <Text fontWeight="medium">
                                  Required Characters Analysis
                                </Text>
                                <Badge>
                                  {
                                    Object.keys(
                                      reportResult.required_hanzi_frequency,
                                    ).length
                                  }
                                </Badge>
                              </HStack>
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                          <AccordionPanel>
                            <TableContainer>
                              <Table size="sm">
                                <Thead>
                                  <Tr>
                                    <Th>Character</Th>
                                    <Th>Frequency</Th>
                                    <Th>Percentage</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {analysisResult &&
                                    Object.entries(
                                      reportResult.required_hanzi_frequency,
                                    ).map(([char, freq]) => {
                                      const percentage = (
                                        (freq /
                                          analysisResult.totalCharacters) *
                                        100
                                      ).toFixed(2);
                                      return (
                                        <Tr key={char}>
                                          <Td fontWeight="bold" fontSize="lg">
                                            {char}
                                          </Td>
                                          <Td>{freq}</Td>
                                          <Td>{percentage}%</Td>
                                        </Tr>
                                      );
                                    })}
                                </Tbody>
                              </Table>
                            </TableContainer>
                          </AccordionPanel>
                        </AccordionItem>

                        <AccordionItem>
                          <AccordionButton>
                            <Box flex="1" textAlign="left">
                              <HStack>
                                <Text fontWeight="medium">
                                  Grade Level Distribution
                                </Text>
                                <Badge colorScheme="blue">
                                  {
                                    Object.keys(
                                      reportResult.known_hanzi_by_grade,
                                    ).length
                                  }
                                </Badge>
                              </HStack>
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                          <AccordionPanel>
                            <TableContainer>
                              <Table size="sm">
                                <Thead>
                                  <Tr>
                                    <Th>Grade Level</Th>
                                    <Th>Character Count</Th>
                                    <Th>Percentage of Total</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {analysisResult &&
                                    Object.entries(
                                      reportResult.known_hanzi_by_grade,
                                    ).map(([grade, count]) => {
                                      const percentage = (
                                        (count /
                                          analysisResult.totalCharacters) *
                                        100
                                      ).toFixed(2);
                                      return (
                                        <Tr key={grade}>
                                          <Td>Grade {grade}</Td>
                                          <Td>{count}</Td>
                                          <Td>{percentage}%</Td>
                                        </Tr>
                                      );
                                    })}
                                </Tbody>
                              </Table>
                            </TableContainer>
                          </AccordionPanel>
                        </AccordionItem>

                        <AccordionItem>
                          <AccordionButton>
                            <Box flex="1" textAlign="left">
                              <HStack>
                                <Text fontWeight="medium">
                                  HSK Level Distribution
                                </Text>
                                <Badge colorScheme="purple">
                                  {hskComparisons.length}
                                </Badge>
                              </HStack>
                            </Box>
                            <AccordionIcon />
                          </AccordionButton>
                          <AccordionPanel>
                            <TableContainer>
                              <Table size="sm">
                                <Thead>
                                  <Tr>
                                    <Th>HSK Level</Th>
                                    <Th>Coverage Rate</Th>
                                    <Th>Difficulty</Th>
                                    <Th>Unknown Words</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {hskComparisons.map((level) => (
                                    <Tr key={level.chapter}>
                                      <Td>HSK {level.chapter}</Td>
                                      <Td>
                                        {(100 - level.coverage).toFixed(1)}%
                                      </Td>
                                      <Td>
                                        <Badge
                                          colorScheme={
                                            level.difficulty < 5
                                              ? 'green'
                                              : level.difficulty < 15
                                              ? 'yellow'
                                              : 'red'
                                          }
                                        >
                                          {level.difficulty < 5
                                            ? 'Low'
                                            : level.difficulty < 15
                                            ? 'Medium'
                                            : 'High'}
                                        </Badge>
                                      </Td>
                                      <Td>{level.unknownWords}</Td>
                                    </Tr>
                                  ))}
                                </Tbody>
                              </Table>
                            </TableContainer>
                          </AccordionPanel>
                        </AccordionItem>
                      </Accordion>
                    </VStack>
                  </Card>
                </VStack>
              )}
            </TabPanel>

            {/* Chapter Comparison Tab */}
            <TabPanel px={0}>
              {hasAnalysis && (
                <VStack spacing={6} align="stretch">
                  <Box>{glossaryTabs}</Box>
                  {resultsGlossary === 'HSK'
                    ? hskComparisons.length > 0 && (
                        <Card>
                          <VStack spacing={4} align="stretch">
                            <HStack>
                              <FiPieChart />
                              <Text fontWeight="bold" fontSize="lg">
                                HSK Level Comparison Analysis
                              </Text>
                              <Tooltip label="Compare text complexity across HSK levels">
                                <Box as="span" display="inline-flex">
                                  <FiInfo />
                                </Box>
                              </Tooltip>
                            </HStack>

                            <Text color="gray.600">
                              Analysis of how the text would perform across HSK
                              levels
                            </Text>

                            <TableContainer>
                              <Table>
                                <Thead>
                                  <Tr>
                                    <Th>HSK Level</Th>
                                    <Th>Coverage Rate</Th>
                                    <Th>Difficulty</Th>
                                    <Th>Unknown Words</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {hskComparisons.map((level) => (
                                    <Tr key={level.chapter}>
                                      <Td fontWeight="bold">
                                        HSK {level.chapter}
                                      </Td>
                                      <Td>
                                        <HStack>
                                          <Text>
                                            {(100 - level.coverage).toFixed(1)}%
                                          </Text>
                                          <Progress
                                            value={100 - level.coverage}
                                            size="sm"
                                            w="60px"
                                            colorScheme={
                                              level.coverage < 10
                                                ? 'green'
                                                : level.coverage < 30
                                                ? 'yellow'
                                                : 'red'
                                            }
                                          />
                                        </HStack>
                                      </Td>
                                      <Td>
                                        <Badge
                                          colorScheme={
                                            level.difficulty < 5
                                              ? 'green'
                                              : level.difficulty < 15
                                              ? 'yellow'
                                              : 'red'
                                          }
                                        >
                                          {level.difficulty < 5
                                            ? 'Low'
                                            : level.difficulty < 15
                                            ? 'Medium'
                                            : 'High'}
                                        </Badge>
                                      </Td>
                                      <Td>{level.unknownWords}</Td>
                                    </Tr>
                                  ))}
                                </Tbody>
                              </Table>
                            </TableContainer>
                          </VStack>
                        </Card>
                      )
                    : chapterComparisons.length > 0 && (
                        <Card>
                          <VStack spacing={4} align="stretch">
                            <HStack>
                              <FiPieChart />
                              <Text fontWeight="bold" fontSize="lg">
                                Chapter Comparison Analysis
                              </Text>
                              <Tooltip label="Compare text complexity across different chapter levels">
                                <Box as="span" display="inline-flex">
                                  <FiInfo />
                                </Box>
                              </Tooltip>
                            </HStack>

                            <Text color="gray.600">
                              Analysis of how the text would perform against
                              different chapter levels
                            </Text>

                            <TableContainer>
                              <Table>
                                <Thead>
                                  <Tr>
                                    <Th>Chapter</Th>
                                    <Th>Coverage Rate</Th>
                                    <Th>Difficulty</Th>
                                    <Th>Unknown Words</Th>
                                    <Th>Status</Th>
                                  </Tr>
                                </Thead>
                                <Tbody>
                                  {chapterComparisons.map((chapter) => (
                                    <Tr
                                      key={chapter.chapter}
                                      bg={
                                        chapter.isSelected
                                          ? 'blue.50'
                                          : 'transparent'
                                      }
                                    >
                                      <Td fontWeight="bold">
                                        {chapter.chapter}
                                        {chapter.isSelected && (
                                          <Badge
                                            ml={2}
                                            colorScheme="blue"
                                            size="sm"
                                          >
                                            Selected
                                          </Badge>
                                        )}
                                      </Td>
                                      <Td>
                                        <HStack>
                                          <Text>
                                            {(100 - chapter.coverage).toFixed(
                                              1,
                                            )}
                                            %
                                          </Text>
                                          <Progress
                                            value={100 - chapter.coverage}
                                            size="sm"
                                            w="60px"
                                            colorScheme={
                                              chapter.coverage < 10
                                                ? 'green'
                                                : chapter.coverage < 30
                                                ? 'yellow'
                                                : 'red'
                                            }
                                          />
                                        </HStack>
                                      </Td>
                                      <Td>
                                        <Badge
                                          colorScheme={
                                            chapter.difficulty < 5
                                              ? 'green'
                                              : chapter.difficulty < 15
                                              ? 'yellow'
                                              : 'red'
                                          }
                                        >
                                          {chapter.difficulty < 5
                                            ? 'Low'
                                            : chapter.difficulty < 15
                                            ? 'Medium'
                                            : 'High'}
                                        </Badge>
                                      </Td>
                                      <Td>{chapter.unknownWords}</Td>
                                      <Td>
                                        <Badge
                                          colorScheme={
                                            chapter.coverage < 10
                                              ? 'green'
                                              : chapter.coverage < 30
                                              ? 'yellow'
                                              : 'red'
                                          }
                                        >
                                          {chapter.coverage < 10
                                            ? 'Excellent'
                                            : chapter.coverage < 30
                                            ? 'Good'
                                            : 'Challenging'}
                                        </Badge>
                                      </Td>
                                    </Tr>
                                  ))}
                                </Tbody>
                              </Table>
                            </TableContainer>
                          </VStack>
                        </Card>
                      )}

                  {/* Recommendations */}
                  {/* <Card>
                    <VStack spacing={4} align="stretch">
                      <Text fontWeight="bold" fontSize="lg">
                        Recommendations
                      </Text>

                      <VStack spacing={3} align="start">
                        {chapterComparisons
                          .filter(
                            (c) =>
                              c.coverage < 20 && c.chapter !== selectedChapter,
                          )
                          .slice(0, 3)
                          .map((chapter) => (
                            <Box
                              key={chapter.chapter}
                              p={3}
                              borderWidth={1}
                              borderRadius="md"
                              borderColor="green.200"
                              bg="green.50"
                            >
                              <HStack justify="space-between">
                                <VStack align="start" spacing={1}>
                                  <Text fontWeight="bold" color="green.700">
                                    Consider Chapter {chapter.chapter}
                                  </Text>
                                  <Text fontSize="sm" color="green.600">
                                    Better coverage (
                                    {(100 - chapter.coverage).toFixed(1)}%) with
                                    lower difficulty
                                  </Text>
                                </VStack>
                                <Badge colorScheme="green">Recommended</Badge>
                              </HStack>
                            </Box>
                          ))}

                        {chapterComparisons.filter((c) => c.coverage < 20)
                          .length === 0 && (
                          <Box
                            p={3}
                            borderWidth={1}
                            borderRadius="md"
                            borderColor="blue.200"
                            bg="blue.50"
                          >
                            <Text color="blue.700">
                              Current chapter selection appears optimal for this
                              text.
                            </Text>
                          </Box>
                        )}
                      </VStack>
                    </VStack>
                  </Card> */}
                </VStack>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>

        {/* Analysis History Modal */}
        <Modal
          isOpen={isHistoryModalOpen}
          onClose={onHistoryModalClose}
          size="6xl"
          isCentered
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>Analysis History</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <VStack spacing={4} align="stretch">
                {analysisHistory.length === 0 ? (
                  <Text textAlign="center" color="gray.500" py={8}>
                    No analysis history available
                  </Text>
                ) : (
                  analysisHistory.map((item) => (
                    <Box
                      key={item.reportId}
                      p={4}
                      borderWidth={1}
                      borderRadius="md"
                    >
                      <HStack justify="space-between" mb={2}>
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="bold">Report #{item.reportId}</Text>
                          <Text fontSize="sm" color="gray.600">
                            {new Date(item.timestamp).toLocaleString()}
                          </Text>
                        </VStack>
                        <HStack>
                          <Badge>Chapter {item.selectedChapter}</Badge>
                          <Badge colorScheme="blue">
                            {item.analysis.coveragePercentage.toFixed(1)}%
                            coverage
                          </Badge>
                        </HStack>
                      </HStack>
                      <Text fontSize="sm" color="gray.700" noOfLines={2}>
                        {item.inputText.substring(0, 200)}...
                      </Text>
                    </Box>
                  ))
                )}
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
      </VStack>
    </AdminLayout>
  );
};
